import { Tooltip } from "flowbite-react";

import { usePilots } from "../../hooks/usePilots";
import { formatCurrency } from "../../utils/currencyUtils";
import { buildDashboardSparkline } from "./constants";
import {
  PilotsIcon,
  ChevronDownIcon,
  DeltaUpIcon,
  DeltaDownIcon,
} from "./icons";

// Sparklines render in a fixed indigo regardless of direction (matches the
// prototype — the trend colour lives in the delta chip, not the line).
const SPARK_COLOR = "#6366f1"; // indigo-500

// Funnel segment colours, strong → faint left-to-right. Index-based so the bar
// works for whatever ordered stages the BE returns; the final stage is muted.
const STAGE_RAMP = [
  "bg-indigo-600",
  "bg-indigo-500",
  "bg-indigo-400",
  "bg-indigo-300",
  "bg-indigo-200",
];
const stageColor = (i, total) =>
  i === total - 1
    ? "bg-slate-200"
    : STAGE_RAMP[Math.min(i, STAGE_RAMP.length - 1)];

const getPilotRevenueTooltip = (revenueUnit) =>
  `Paid PoCs shown as ${revenueUnit}. They have not yet converted to annual subscriptions and are not included in NRR or Churn.`;

const TOOLTIPS = {
  // PAN-800: the per-quarter PoC rate (pilot_conversion_rate_pct.window === "quarter").
  conversion:
    "% of PoCs ending this quarter that converted. Starts at last quarter's rate and moves with every win or loss; a PoC past its end date without a decision counts as not converted.",
};

// Ignore the BE-preformatted `.formatted` string on purpose — it's the un-abbreviated form
// ("€8,600"), and the pilot MRR tile should match sibling metric tiles that use the FE-side
// abbreviated formatter ("€8.6K") from currencyUtils.
const formatMoney = (money, revenueUnit) => {
  if (!money) return "—";
  const amount =
    revenueUnit === "ARR" ? Number(money.amount) * 12 : money.amount;
  return formatCurrency(amount, money.currency);
};

// Normalise the team-revenue funnel list ([{ stage_id, name, count }]) into rows.
const buildPilotStages = (teamRevenue) =>
  (teamRevenue?.pilot_stage_counts ?? []).map((s, i) => ({
    key: s.stage_id ?? s.id ?? s.stage ?? `stage-${i}`,
    label: s.name ?? s.label ?? s.stage ?? "—",
    count: s.count ?? 0,
  }));

/**
 * Map the two pilot sources into the tiles + funnel (BE pano #1075).
 *
 * From /priority-dashboard/summary/ (`summary`):
 *   - Pilot MRR         ← evaluating_mrr (Money obj)
 *   - Active Pilots     ← active_pilots
 *   - Pilot MRR at Risk ← at_risk_mrr (Money obj, PAN-435 headline metric) +
 *                         pilots_at_risk (count, secondary) +
 *                         decision_due_days (secondary, hides when null)
 * From /metrics/team-revenue/ (`teamRevenue`):
 *   - Conversion Rate ← pilot_conversion_rate_pct.{value, estimated, historical_data}
 *                       (no change chip — PAN-800; the per-quarter PoC rate)
 *   - Pipeline bar    ← pilot_stage_counts
 *
 * Every field is null-safe so the card degrades to "—"/0 / "None at risk" when a
 * source (or field) is missing. Pure + data-driven → the component parses nothing.
 */
const buildPilotMetrics = (summary, teamRevenue, revenueUnit) => {
  if (!summary && !teamRevenue) return { tiles: [], stages: [] };

  const conversion = teamRevenue?.pilot_conversion_rate_pct ?? null;
  const stages = buildPilotStages(teamRevenue);

  // PAN-435: headline is €MRR at risk; secondary line combines the pilot count
  // with the decision-due countdown. When decisionDueDays is null (no at-risk
  // pilots, none have a pilot_end_date, or team hasn't mapped the property)
  // the "decision due" clause hides — leaving just "N pilots" or "None at risk".
  const atRiskCount = summary?.pilots_at_risk ?? 0;
  const decisionDueDays = summary?.decision_due_days;
  let atRiskCaption;
  if (atRiskCount === 0) {
    atRiskCaption = "None at risk";
  } else {
    const pilotsPart = `${atRiskCount} ${atRiskCount === 1 ? "pilot" : "pilots"}`;
    const duePart =
      decisionDueDays === null || decisionDueDays === undefined
        ? null
        : `decision due ≤ ${decisionDueDays} days`;
    atRiskCaption = duePart ? `${pilotsPart} · ${duePart}` : pilotsPart;
  }

  const tiles = [
    {
      id: "pilotMrr",
      label: `Pilot ${revenueUnit}`,
      tooltip: getPilotRevenueTooltip(revenueUnit),
      value: formatMoney(summary?.evaluating_mrr, revenueUnit),
      delta: null,
      sparkline: null,
      caption: "Paid pilots in evaluation",
    },
    {
      id: "activePilots",
      label: "Active Pilots",
      tooltip: null,
      value: `${summary?.active_pilots ?? 0}`,
      delta: null,
      sparkline: null,
      caption: "Currently in evaluation",
    },
    {
      id: "conversionRate",
      label: "Pilot Conversion Rate",
      tooltip: TOOLTIPS.conversion,
      value:
        conversion?.value != null ? `${Math.round(conversion.value)}%` : "—",
      delta: null,
      // PAN-800: the quarter's rate still leans on last quarter's while any PoC is open.
      estimated: conversion?.estimated === true,
      sparkline: buildDashboardSparkline(conversion?.historical_data),
      // PAN-800: the per-quarter tile needs no caption (Ben, 2026-10-09).
      caption: null,
    },
    {
      id: "pilotMrrAtRisk",
      label: `Pilot ${revenueUnit} at Risk`,
      tooltip: null,
      // Headline: money-at-stake, not count. Falls back to "—" when the BE hasn't
      // populated at_risk_mrr yet (auto-hide-friendly).
      value:
        atRiskCount === 0
          ? "—"
          : formatMoney(summary?.at_risk_mrr, revenueUnit),
      delta: null,
      sparkline: null,
      caption: atRiskCaption,
    },
  ];

  return { tiles, stages };
};

const DeltaChip = ({ delta }) => {
  if (!delta) return null;
  const colorClass =
    delta.direction === "up"
      ? "text-emerald-600"
      : delta.direction === "down"
        ? "text-red-600"
        : "text-slate-400";
  return (
    <span
      className={`inline-flex items-center gap-0.5 text-xs font-bold ${colorClass}`}
    >
      {delta.direction === "up" && <DeltaUpIcon />}
      {delta.direction === "down" && <DeltaDownIcon />}
      {delta.text}
    </span>
  );
};

const InfoDot = ({ content, label }) => (
  <Tooltip
    content={content}
    // eslint-disable-next-line react/style-prop-object
    style="light"
  >
    <span
      className="inline-flex cursor-help items-center text-slate-400 hover:text-slate-500"
      role="img"
      aria-label={label}
    >
      <svg
        className="h-3.5 w-3.5"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={2}
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
    </span>
  </Tooltip>
);

const MetricCell = ({ metric }) => (
  <div className="bg-white px-6 py-[18px]">
    <div className="mb-1.5 flex items-center gap-1.5">
      <span className="text-[11px] font-semibold uppercase tracking-[0.5px] text-slate-400">
        {metric.label}
      </span>
      {metric.estimated && (
        <span className="text-[11px] font-normal text-slate-500">
          Estimated
        </span>
      )}
      {metric.tooltip && (
        <InfoDot
          content={metric.tooltip}
          label={`${metric.label} information`}
        />
      )}
    </div>
    <div className="mb-2.5 flex items-baseline gap-2">
      <span className="text-[26px] font-extrabold tracking-[-0.8px] text-slate-800">
        {metric.value}
      </span>
      <DeltaChip delta={metric.delta} />
    </div>
    {metric.sparkline && (
      <div className="relative">
        <svg
          width="100%"
          height="32"
          viewBox="0 0 200 32"
          preserveAspectRatio="none"
          className="block"
        >
          <defs>
            <linearGradient
              id={`pilotSpark-${metric.id}`}
              x1="0"
              y1="0"
              x2="0"
              y2="1"
            >
              <stop offset="0%" stopColor={SPARK_COLOR} stopOpacity="0.15" />
              <stop offset="100%" stopColor={SPARK_COLOR} stopOpacity="0" />
            </linearGradient>
          </defs>
          <path
            d={metric.sparkline.linePath}
            fill="none"
            stroke={SPARK_COLOR}
            strokeWidth="2"
          />
          <path
            d={metric.sparkline.areaPath}
            fill={`url(#pilotSpark-${metric.id})`}
          />
        </svg>
        {metric.sparkline.endPoint && (
          <span
            className="pointer-events-none absolute h-1 w-1 -translate-x-1/2 -translate-y-1/2 rounded-full"
            style={{
              left: `${(metric.sparkline.endPoint.x / 200) * 100}%`,
              top: `${(metric.sparkline.endPoint.y / 32) * 100}%`,
              backgroundColor: SPARK_COLOR,
            }}
          />
        )}
      </div>
    )}
    {metric.caption && (
      <div className="mt-1.5 text-[11px] text-slate-400">{metric.caption}</div>
    )}
  </div>
);

const PipelineBar = ({ stages }) => {
  const total = stages.reduce((sum, s) => sum + (s.count || 0), 0);

  return (
    <div className="border-t border-slate-100 px-6 py-[15px]">
      <div className="mb-3 text-[11px] font-semibold uppercase tracking-[0.5px] text-slate-400">
        Pipeline
      </div>
      <div className="flex h-3 gap-0.5 overflow-hidden rounded-full">
        {total === 0 ? (
          <div className="flex-1 bg-slate-100" />
        ) : (
          stages
            .map((s, i) => ({ ...s, color: stageColor(i, stages.length) }))
            .filter((s) => s.count > 0)
            .map((s) => (
              <div
                key={s.key}
                title={`${s.label}: ${s.count}`}
                className={`min-w-[8px] ${s.color}`}
                style={{ flex: `${s.count} 1 0%` }}
              />
            ))
        )}
      </div>
      <div className="mt-3 flex flex-wrap gap-x-[18px] gap-y-2">
        {stages.map((s, i) => {
          const isEmpty = !s.count;
          return (
            <span
              key={s.key}
              className={`flex items-center gap-1.5 text-xs ${
                isEmpty ? "text-slate-400" : "text-slate-600"
              }`}
            >
              <span
                className={`h-[9px] w-[9px] rounded-sm ${stageColor(i, stages.length)}`}
              />
              {s.label}{" "}
              <b
                className={`font-bold ${isEmpty ? "text-slate-400" : "text-slate-900"}`}
              >
                {s.count}
              </b>
            </span>
          );
        })}
      </div>
    </div>
  );
};

const LoadingSkeleton = () => (
  <div data-testid="pilots-loading">
    <div className="grid grid-cols-4 gap-px border-t border-slate-100 bg-slate-100">
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="bg-white px-6 py-[18px]">
          <div className="mb-1.5 h-3 w-24 animate-pulse rounded bg-slate-200" />
          <div className="mb-2.5 flex items-baseline gap-2">
            <div className="h-7 w-24 animate-pulse rounded bg-slate-200" />
          </div>
          <div className="h-8 w-full animate-pulse rounded bg-slate-100" />
          <div className="mt-1.5 h-3 w-28 animate-pulse rounded bg-slate-200" />
        </div>
      ))}
    </div>
    <div className="border-t border-slate-100 px-6 py-[15px]">
      <div className="mb-3 h-3 w-16 animate-pulse rounded bg-slate-200" />
      <div className="h-3 w-full animate-pulse rounded-full bg-slate-100" />
    </div>
  </div>
);

export const Pilots = ({ expanded, onToggle, revenueUnit = "MRR" }) => {
  const { summary, teamRevenue, loading, error } = usePilots();
  const { tiles, stages } = buildPilotMetrics(
    summary,
    teamRevenue,
    revenueUnit,
  );

  return (
    <div className="mb-5">
      <div className="overflow-hidden rounded-[14px] border border-[#e8ecf0] bg-white">
        <button
          onClick={onToggle}
          className="flex w-full cursor-pointer items-center justify-between border-none bg-transparent px-6 pb-3 pt-4"
        >
          <div className="flex items-center gap-2">
            <span className="text-slate-600">
              <PilotsIcon />
            </span>
            <span className="text-[13px] font-bold text-slate-800">Pilots</span>
          </div>
          <div className="flex items-center gap-4">
            {!expanded && !loading && tiles.length > 0 && (
              <div className="flex gap-4">
                {tiles.map((metric) => (
                  <div
                    key={metric.id}
                    className="flex items-baseline gap-1 text-[11px]"
                  >
                    <span className="font-medium text-slate-400">
                      {metric.label}
                    </span>
                    <span className="text-sm font-bold text-black">
                      {metric.value}
                    </span>
                  </div>
                ))}
              </div>
            )}
            <ChevronDownIcon expanded={expanded} />
          </div>
        </button>

        {expanded && (
          <>
            {loading && <LoadingSkeleton />}
            {error && !loading && (
              <div className="border-t border-slate-100 px-6 py-8 text-center text-sm text-red-500">
                Failed to load pilots. Please try again later.
              </div>
            )}
            {!loading && !error && tiles.length > 0 && (
              <>
                <div className="grid grid-cols-4 gap-px border-t border-slate-100 bg-slate-100">
                  {tiles.map((metric) => (
                    <MetricCell key={metric.id} metric={metric} />
                  ))}
                </div>
                {stages.length > 0 && <PipelineBar stages={stages} />}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};
